import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { h2hAPI } from '../api/endpoints';
import { formatXPUNT, formatTimeAgo, formatDate } from '../utils/helpers';
import { 
  FaArrowLeft, FaUsers, FaTrophy, FaClock, 
  FaHandshake, FaCheck, FaTimes, FaCopy, FaShare,
  FaChartBar, FaCoins, FaUser
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './H2HDetail.css';

const H2HDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [joined, setJoined] = useState(false);
  const [userStake, setUserStake] = useState(null);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [stakeAmount, setStakeAmount] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [claimStatus, setClaimStatus] = useState(null);

  const fetchGameData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await h2hAPI.getList({ per_page: 1 });
      const gameData = response.data.data?.find(g => g.id === parseInt(id));
      if (gameData) {
        setGame(gameData);
      }

      if (isAuthenticated) {
        // Check if user joined
        const joinedRes = await h2hAPI.getList({ per_page: 1 });
        // This would need a proper endpoint to check join status
        // For now we'll simulate
        setJoined(false);
      }
    } catch (error) {
      console.error('Failed to fetch game:', error);
      toast.error('Failed to load game');
      navigate('/h2h');
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, navigate]);

  useEffect(() => {
    fetchGameData();
  }, [fetchGameData]);

  const handleJoin = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to join');
      navigate('/login');
      return;
    }

    const stake = parseFloat(stakeAmount);
    if (!stake || stake < game?.minimum_stake) {
      toast.error(`Minimum stake is ${formatXPUNT(game?.minimum_stake)}`);
      return;
    }
    if (!selectedOutcome) {
      toast.error('Please select an outcome');
      return;
    }

    try {
      await h2hAPI.join(id, { stake, outcome: selectedOutcome });
      setJoined(true);
      setShowJoinForm(false);
      toast.success('Joined H2H game successfully!');
      fetchGameData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to join');
    }
  };

  const handleClaim = async () => {
    try {
      const response = await h2hAPI.claim(id);
      toast.success('Winnings claimed successfully!');
      setClaimStatus(response.data);
      fetchGameData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to claim');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: game?.title || 'H2H Game',
        text: `${game?.home_team} vs ${game?.away_team} - Join the H2H!`,
        url: url,
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    }
  };

  if (loading) {
    return (
      <div className="h2h-detail-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="h2h-not-found">
        <h2>Game not found</h2>
        <Link to="/h2h" className="btn btn-primary">
          <FaArrowLeft /> Back to H2H
        </Link>
      </div>
    );
  }

  const isCreator = user && game.author_id === user.id;
  const canJoin = game.status === 'open' && !joined && isAuthenticated;

  return (
    <div className="h2h-detail-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="h2h-detail-grid">
        {/* Main Content */}
        <div className="detail-main">
          <div className="detail-card">
            {/* Header */}
            <div className="detail-header">
              <div className="detail-meta">
                <span className={`h2h-status ${game.status}`}>
                  {game.status}
                </span>
                {game.has_started && (
                  <span className="h2h-started">🔴 Live</span>
                )}
              </div>
              <div className="detail-actions">
                <button onClick={handleShare} className="action-btn">
                  <FaShare />
                </button>
              </div>
            </div>

            <h1 className="detail-title">
              {game.home_team} vs {game.away_team}
            </h1>

            {/* Stats */}
            <div className="detail-stats">
              <div className="stat-item">
                <FaTrophy className="stat-icon" />
                <div>
                  <span className="stat-value">{formatXPUNT(game.total_pot)}</span>
                  <span className="stat-label">Total Pot</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaUsers className="stat-icon" />
                <div>
                  <span className="stat-value">{game.players_count || 0}</span>
                  <span className="stat-label">Players</span>
                </div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <FaClock className="stat-icon" />
                <div>
                  <span className="stat-value">{formatTimeAgo(game.created_at)}</span>
                  <span className="stat-label">Created</span>
                </div>
              </div>
            </div>

            {/* Game Info */}
            <div className="game-info">
              {game.kickoff_time && (
                <div className="info-row">
                  <span>⏰ Kickoff</span>
                  <span>{formatDate(game.kickoff_time)}</span>
                </div>
              )}
              {game.minimum_stake && (
                <div className="info-row">
                  <span>💰 Minimum Stake</span>
                  <span className="gold">{formatXPUNT(game.minimum_stake)}</span>
                </div>
              )}
              {game.result && (
                <div className="info-row">
                  <span>🏆 Result</span>
                  <span className="result">{game.result}</span>
                </div>
              )}
              {game.scoreline && (
                <div className="info-row">
                  <span>⚽ Score</span>
                  <span className="score">{game.scoreline}</span>
                </div>
              )}
            </div>

            {/* Join Section */}
            {canJoin && (
              <div className="join-section">
                <button 
                  onClick={() => setShowJoinForm(!showJoinForm)}
                  className="btn btn-primary btn-lg"
                >
                  {showJoinForm ? 'Cancel' : 'Join Game'}
                </button>
              </div>
            )}

            {joined && (
              <div className="joined-badge">
                <FaCheck /> You've joined this game
                {claimStatus?.has_winnings && !claimStatus?.claimed && (
                  <button onClick={handleClaim} className="btn btn-success btn-sm">
                    Claim Winnings
                  </button>
                )}
              </div>
            )}

            {/* Join Form */}
            {showJoinForm && canJoin && (
              <div className="join-form">
                <h3>Join H2H Game</h3>
                <div className="form-group">
                  <label>Select Outcome</label>
                  <div className="outcome-buttons">
                    {['Home Win', 'Draw', 'Away Win'].map(outcome => (
                      <button
                        key={outcome}
                        className={`outcome-btn ${selectedOutcome === outcome ? 'selected' : ''}`}
                        onClick={() => setSelectedOutcome(outcome)}
                      >
                        {outcome}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Stake Amount ({formatXPUNT(game.minimum_stake)} minimum)</label>
                  <input
                    type="number"
                    placeholder="Enter stake amount"
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(e.target.value)}
                    min={game.minimum_stake}
                    step="0.01"
                  />
                </div>
                <div className="form-actions">
                  <button 
                    onClick={() => setShowJoinForm(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleJoin}
                    className="btn btn-primary"
                    disabled={!selectedOutcome || !stakeAmount}
                  >
                    Join Game
                  </button>
                </div>
              </div>
            )}

            {/* Stakes Distribution */}
            {game.stakes && Object.keys(game.stakes).length > 0 && (
              <div className="stakes-section">
                <h3>📊 Stakes Distribution</h3>
                <div className="stakes-bars">
                  {Object.entries(game.stakes).map(([outcome, data]) => {
                    const total = Object.values(game.stakes).reduce((sum, d) => sum + d.total, 0);
                    const percentage = total > 0 ? (data.total / total) * 100 : 0;
                    return (
                      <div key={outcome} className="stake-bar-item">
                        <div className="stake-bar-header">
                          <span className="stake-outcome">{outcome}</span>
                          <span className="stake-amount">{formatXPUNT(data.total)}</span>
                          <span className="stake-players">{data.players} players</span>
                        </div>
                        <div className="stake-bar-track">
                          <div 
                            className="stake-bar-fill"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="detail-sidebar">
          <div className="info-card">
            <h4>ℹ️ Game Info</h4>
            <div className="info-item">
              <span>Created By</span>
              <span>{game.author}</span>
            </div>
            <div className="info-item">
              <span>Status</span>
              <span className={game.status}>{game.status}</span>
            </div>
            <div className="info-item">
              <span>Players</span>
              <span>{game.players_count || 0}</span>
            </div>
            {game.total_pot && (
              <div className="info-item">
                <span>Total Pot</span>
                <span className="gold">{formatXPUNT(game.total_pot)}</span>
              </div>
            )}
          </div>

          {isCreator && game.status === 'open' && (
            <div className="info-card creator-actions">
              <h4>🎯 Creator Actions</h4>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => {
                  // Navigate to set result
                }}
              >
                Set Result
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default H2HDetail;